import i18nFactory from '@yandex-int/i18n';
import moment from 'moment';

import { RadioButtonValue } from '../../components/RadioButtonGroup/RadioButtonGroup';
import { DictionaryItem } from '../../typings/common';
import { GdCampaignType, GdStringDictNameInput } from '../../typings/graphql-grid';
import {
    AdditionalTargetingField,
    ApplicationStore,
    OsType,
    SuggestTargetingKey,
    SuggestWithStringDictTargetingKey,
    SuggestWithVersionTargetingKey,
    TagCloudTargetingToggleEditorValue
} from '../../typings/internalGroups';
import { AppInfo } from '../../typings/mobile-apps';
import { TagValue, TargetingTagType, TargetingTagValue, TargetingToggle } from '../../typings/tagCloudTargeting';
import { isTagExcluded, isTagIncluded, mapTagValueToString } from '../TagCloudTargetingHelper/TagCloudTargetingHelper';

import * as keyset from './InternalGroupHelpers.i18n';

const i18n = i18nFactory(keyset);

export const getRenderValueForShowDates = (tag: TagValue): TagValue => {
    return [moment(mapTagValueToString(tag), 'YYYY-MM-DD').format('DD.MM.YYYY')];
};

export const mapUserInputForShowDates = (tag: TagValue): TagValue => {
    return [moment(mapTagValueToString(tag), 'DD.MM.YYYY').format('YYYY-MM-DD')];
};

export const showDatesTagValidate = (tag: TagValue): boolean => {
    return mapTagValueToString(tag) !== 'Invalid date';
};

export const numericTagValidate = (tag: TagValue): boolean => {
    return /^[0-9]+$/.test(mapTagValueToString(tag));
};

export const mapShowDatesOnSave = (tags: TargetingTagValue[]): TargetingTagValue[] => {
    return tags
        .filter(tag => mapTagValueToString(tag.value) !== 'Invalid date')
        .sort((a, b) => moment(mapTagValueToString(a.value)).diff(moment(mapTagValueToString(b.value))));
};

export const isInternalGroup = (type: GdCampaignType): boolean => {
    return type === GdCampaignType.INTERNAL_FREE || type === GdCampaignType.INTERNAL_DISTRIB;
};

export const getRadioTargetingItems = (): RadioButtonValue[] => {
    return [
        {
            id: TagCloudTargetingToggleEditorValue.YES,
            name: i18n('yes')
        },
        {
            id: TagCloudTargetingToggleEditorValue.NO,
            name: i18n('no')
        },
        {
            id: TagCloudTargetingToggleEditorValue.NOT_SELECTED,
            name: i18n('not-selected')
        }
    ];
};

export const getValueForToggle = (toggleId: string, tags: TargetingTagValue[]): TagCloudTargetingToggleEditorValue => {
    const toggleValueTag = tags.find(tag => tag.value === toggleId);

    if (!toggleValueTag) {
        return TagCloudTargetingToggleEditorValue.NOT_SELECTED;
    }

    return toggleValueTag.type === TargetingTagType.INCLUDED
        ? TagCloudTargetingToggleEditorValue.YES
        : TagCloudTargetingToggleEditorValue.NO;
};

export const getRadioButtonGroupKey = (groupValue: string, toggleId: string, tags: TargetingTagValue[]): string => {
    const includedValue = tags.filter(isTagIncluded);
    const excludedValue = tags.filter(isTagExcluded);

    return `${toggleId}_${groupValue}_${includedValue.length}_${excludedValue.length}`;
};

export const fetchInterfaceLangs = (): DictionaryItem[] => {
    return [
        {
            val: 'BY',
            text: 'BY'
        },
        {
            val: 'EN',
            text: 'EN'
        },
        {
            val: 'KZ',
            text: 'KZ'
        },
        {
            val: 'RU',
            text: 'RU'
        },
        {
            val: 'TR',
            text: 'TR'
        },
        {
            val: 'TT',
            text: 'TT'
        },
        {
            val: 'UK',
            text: 'UK'
        }
    ];
};

export const getInternalNetworkToggles = (): TargetingToggle[] => {
    return [
        {
            id: 'targetingInternalNetwork',
            title: i18n('internal-network')
        }
    ];
};

export const mapInternalNetworkTagValue = (value: TagValue): string => {
    const toggle = getInternalNetworkToggles().find(it => it.id === value);

    return toggle ? toggle.title : '';
};

export const getDeviceToggles = (): TargetingToggle[] => {
    return [
        {
            id: 'targetingIsMobile',
            title: i18n('is-mobile')
        },
        {
            id: 'targetingIsTablet',
            title: i18n('is-tablet')
        },
        {
            id: 'targetingIsTouch',
            title: i18n('is-touch')
        }
    ];
};

export const mapDeviceTagValue = (value: TagValue): string => {
    const toggle = getDeviceToggles().find(it => it.id === value);

    return toggle ? toggle.title : '';
};

export const getYandexPlusToggles = (): TargetingToggle[] => {
    return [
        {
            id: 'targetingIsYandexPlus',
            title: i18n('is-yandex-plus')
        }
    ];
};

export const getIsVirusedToggles = (): TargetingToggle[] => {
    return [
        {
            id: 'targetingIsVirused',
            title: i18n('is-virused')
        }
    ];
};

export const mapYandexPlusTagValue = (value: TagValue): string => {
    const toggle = getYandexPlusToggles().find(it => it.id === value);

    return toggle ? toggle.title : '';
};

export const mapIsVirusedTagValue = (value: TagValue): string => {
    const toggle = getIsVirusedToggles().find(it => it.id === value);

    return toggle ? toggle.title : '';
};

export const getStringDictByTargetingKey = (key: AdditionalTargetingField): GdStringDictNameInput | undefined => {
    switch (key) {
        case AdditionalTargetingField.BROWSER_ENGINES:
            return GdStringDictNameInput.UA_TRAITS_BROWSER_ENGINE;

        case AdditionalTargetingField.BROWSER_NAMES:
            return GdStringDictNameInput.UA_TRAITS_BROWSER_NAME;

        case AdditionalTargetingField.DEVICE_VENDORS:
            return GdStringDictNameInput.UA_TRAITS_DEVICE_VENDOR;

        case AdditionalTargetingField.DESKTOP_INSTALLED_APPS:
            return GdStringDictNameInput.DISTRIB_SOFT;

        case AdditionalTargetingField.OS_NAMES:
            return GdStringDictNameInput.UA_TRAITS_OS_NAME;

        case AdditionalTargetingField.OS_FAMILIES:
            return GdStringDictNameInput.UA_TRAITS_OS_FAMILY;

        default:
            return undefined;
    }
};

export const getAppStoreName = (osType?: string): ApplicationStore | undefined => {
    if (osType === OsType.ANDROID) {
        return ApplicationStore.GOOGLE_PLAY;
    }

    if (osType === OsType.IOS) {
        return ApplicationStore.APP_STORE;
    }

    return undefined;
};

type ValidationFn = (tagValue: TagValue) => boolean;
type TagMapperFn = (tagValue: TagValue) => TagValue;

export const mobileAppTagValidationFactory = (apps: AppInfo[]): ValidationFn => {
    return (tagValue: TagValue): boolean => {
        const internalValue = mapTagValueToString(tagValue);
        const appInfo = apps.find(app => (app.appUrl ? internalValue.includes(app.appUrl) : false));

        return !Boolean(appInfo && appInfo.errors && appInfo.errors.find(error => Boolean(error.code)));
    };
};

export const mobileAppTagRenderedValueFactory = (apps: AppInfo[]): TagMapperFn => {
    return (tagValue: TagValue): TagValue => {
        const internalValue = mapTagValueToString(tagValue);
        const appInfo = apps.find(app => (app.appUrl ? internalValue.includes(app.appUrl) : false));

        if (!appInfo || (appInfo.errors && appInfo.errors.find(error => Boolean(error.code)))) {
            return i18n('invalid-url');
        }

        const appStore = getAppStoreName(appInfo.osType);

        if (!appStore) {
            return i18n('invalid-url');
        }

        return [`${appInfo.name} (${appStore})`];
    };
};

export const getStringDictNameByTargetingKey = (
    key: SuggestWithStringDictTargetingKey | SuggestTargetingKey | SuggestWithVersionTargetingKey
): GdStringDictNameInput | undefined => {
    switch (key) {
        case AdditionalTargetingField.OS_NAMES:
            return GdStringDictNameInput.UA_TRAITS_OS_NAME;
        case AdditionalTargetingField.OS_FAMILIES:
            return GdStringDictNameInput.UA_TRAITS_OS_FAMILY;
        case AdditionalTargetingField.BROWSER_NAMES:
            return GdStringDictNameInput.UA_TRAITS_BROWSER_NAME;
        case AdditionalTargetingField.BROWSER_ENGINES:
            return GdStringDictNameInput.UA_TRAITS_BROWSER_ENGINE;
        case AdditionalTargetingField.DEVICE_VENDORS:
            return GdStringDictNameInput.UA_TRAITS_DEVICE_VENDOR;
        case AdditionalTargetingField.DESKTOP_INSTALLED_APPS:
            return GdStringDictNameInput.DISTRIB_SOFT;
        default:
            return undefined;
    }
};

export const INTERNAL_GROUP_TEXT_FIELDS = [
    AdditionalTargetingField.SHOW_DATES,
    AdditionalTargetingField.YANDEX_UIDS,
    AdditionalTargetingField.DEVICE_NAMES,
    AdditionalTargetingField.USER_AGENTS,
    AdditionalTargetingField.QUERY_REFERERS,
    AdditionalTargetingField.CLID_TYPES,
    AdditionalTargetingField.CLIDS,
    AdditionalTargetingField.MOBILE_INSTALLED_APPS,
    AdditionalTargetingField.QUERY_OPTIONS,
    AdditionalTargetingField.TEST_IDS,
    AdditionalTargetingField.YS_COOKIES
];

export const INTERNAL_GROUP_SUGGEST_FIELDS = [
    AdditionalTargetingField.INTERFACE_LANGS,
    AdditionalTargetingField.DESKTOP_INSTALLED_APPS,
    AdditionalTargetingField.OS_NAMES,
    AdditionalTargetingField.DEVICE_VENDORS
];

export const INTERNAL_GROUP_SUGGEST_WITH_VERSION_FIELDS = [
    AdditionalTargetingField.OS_FAMILIES,
    AdditionalTargetingField.BROWSER_ENGINES,
    AdditionalTargetingField.BROWSER_NAMES
];
